<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3';
import { computed, onMounted } from 'vue';
import { useSentryFeedback } from '@/composables/useSentryFeedback';

const props = defineProps<{
    status: number;
    sentryEventId?: string | null;
}>();

const { openCrashReport } = useSentryFeedback();

onMounted(() => {
    if (props.status === 500 && props.sentryEventId) {
        openCrashReport(props.sentryEventId);
    }
});

const title = computed(() => {
    return (
        {
            503: '503 : Service indisponible',
            500: '500 : Erreur serveur',
            404: '404 : Page introuvable',
            403: '403 : Accès interdit',
        }[props.status] ?? `${props.status} : Erreur`
    );
});

const description = computed(() => {
    return (
        {
            503: 'Nous effectuons une maintenance. Veuillez réessayer dans quelques instants.',
            500: "Une erreur inattendue s'est produite sur nos serveurs.",
            404: 'La page que vous recherchez est introuvable.',
            403: "Vous n'êtes pas autorisé à accéder à cette page.",
        }[props.status] ?? "Une erreur s'est produite."
    );
});
</script>

<template>
    <Head :title="title" />
    <div
        class="flex min-h-screen flex-col items-center justify-center bg-background p-6 text-foreground"
    >
        <div class="w-full max-w-md text-center">
            <p class="text-8xl font-bold text-muted-foreground">
                {{ status }}
            </p>
            <h1 class="mt-4 text-2xl font-semibold">
                {{ title }}
            </h1>
            <p class="mt-2 text-muted-foreground">
                {{ description }}
            </p>
            <div class="mt-8 flex flex-col items-center gap-3">
                <Link
                    v-if="status !== 503"
                    href="/"
                    class="inline-block rounded-sm border border-border px-5 py-1.5 text-sm leading-normal hover:border-border/80"
                >
                    Retour à l'accueil
                </Link>
                <button
                    v-if="status === 500 && sentryEventId"
                    class="text-sm text-muted-foreground underline-offset-2 hover:underline"
                    type="button"
                    @click="openCrashReport(sentryEventId!)"
                >
                    Signaler le problème
                </button>
            </div>
        </div>
    </div>
</template>
